import { logger } from '@/utils/logger';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from './safeStorage';
import { Message, MessageType } from '@/types/Message';
import {
  filterMessages,
  filterMessagesByType,
  latestMessage,
  latestMessageByType,
  normalSortedMessages,
  orderedMessagesByType,
} from '@/helpers/messages';
import { parseMessageTimestamp } from '@/helpers/timestamp';

interface MessagesState {
  messages: Message[];
  loading: boolean;
  error: string | null;
  room: string | null;
  paginationCursor: string | null;
}

interface MessagesActions {
  loadMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setRoom: (room: string | null) => void;
  setPaginationCursor: (cursor: string | null) => void;
}

interface MessagesSelectors {
  getMessagesByType: (type: MessageType) => Message[];
  getLatestMessage: () => Message | undefined;
  getLatestMessageByType: (type: MessageType) => Message | undefined;
  getFilteredMessages: (predicate: (message: Message) => boolean) => Message[];
  getOrderedMessagesByType: (type: string, order?: 'ASC' | 'DESC') => Message[];
}

interface MessagesStore extends MessagesState, MessagesActions, MessagesSelectors {}

const initialState: MessagesState = {
  messages: [],
  loading: true,
  error: null,
  room: null,
  paginationCursor: null,
};

export const useMessagesStore = create<MessagesStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Actions
      loadMessages: (messages) => {
        // Cast messages to proper type since Firebase returns any[]
        const typedMessages = messages as Message[];
        const realMessages = typedMessages.filter((msg) => !msg.id?.startsWith('optimistic-'));

        // Preserve optimistic messages still awaiting their Firestore counterpart
        // (offline sends never show up in the real query until the server timestamp
        // is assigned). Drop each once a real message with the same author+text arrives.
        const { messages: current } = get();
        const realKeys = new Set(realMessages.map((msg) => `${msg.uid}|${msg.text}`));
        const pendingMessages = current.filter(
          (msg) => msg.id?.startsWith('optimistic-') && !realKeys.has(`${msg.uid}|${msg.text}`)
        );

        const sortedMessages = normalSortedMessages([...realMessages, ...pendingMessages]);
        set({
          messages: sortedMessages,
          loading: false,
          error: null,
        });
      },

      addMessage: (message) => {
        const { messages } = get();
        // Check for duplicate messages to prevent unnecessary updates
        const existingMessage = messages.find((msg) => msg.id === message.id);
        if (existingMessage) {
          return; // Message already exists, no update needed
        }

        const newMessages = [...messages, message];
        const sortedMessages = normalSortedMessages(newMessages);
        set({ messages: sortedMessages });
      },

      updateMessage: (id, updates) => {
        const { messages } = get();
        let hasChanges = false;
        const updatedMessages = messages.map((msg) => {
          if (msg.id === id) {
            // Check if updates actually change anything
            const updatedMsg = { ...msg, ...updates } as Message;
            const hasActualChanges = Object.keys(updates).some(
              (key) => (msg as any)[key] !== (updates as any)[key]
            );
            if (hasActualChanges) {
              hasChanges = true;
              return updatedMsg;
            }
          }
          return msg;
        });

        // Only update state if there were actual changes
        if (hasChanges) {
          const sortedMessages = normalSortedMessages(updatedMessages);
          set({ messages: sortedMessages });
        }
      },

      clearMessages: () => {
        set({ messages: [], loading: false, error: null });
      },

      setLoading: (loading) => {
        const { loading: currentLoading } = get();
        if (currentLoading !== loading) {
          set({ loading });
        }
      },

      setError: (error) => {
        const { error: currentError } = get();
        if (currentError !== error) {
          set({ error });
        }
      },

      setRoom: (room) => {
        const { room: currentRoom } = get();
        if (currentRoom !== room) {
          set({ room });
        }
      },

      setPaginationCursor: (cursor) => {
        const { paginationCursor: currentCursor } = get();
        if (currentCursor !== cursor) {
          set({ paginationCursor: cursor });
        }
      },

      // Selectors - Optimized for performance
      getMessagesByType: (type) => {
        const { messages } = get();
        return filterMessagesByType(messages, type);
      },

      getLatestMessage: () => {
        const { messages } = get();
        return latestMessage(messages);
      },

      getLatestMessageByType: (type) => {
        const { messages } = get();
        return latestMessageByType(messages, type);
      },

      getFilteredMessages: (predicate) => {
        const { messages } = get();
        return filterMessages(messages, predicate);
      },

      getOrderedMessagesByType: (type, order = 'ASC') => {
        const { messages } = get();
        return orderedMessagesByType(messages, type, order);
      },
    }),
    {
      name: 'messages-storage', // localStorage key
      storage: persistStorage,
      partialize: (state) => ({
        messages: state.messages,
        room: state.room,
        paginationCursor: state.paginationCursor,
      }),
      // Add TTL functionality
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            // Clear messages older than 24 hours on rehydration
            const twentyFourHoursAgo = new Date();
            twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

            const validMessages = state.messages.filter((msg) => {
              try {
                const messageTime = parseMessageTimestamp(msg.timestamp);
                if (!messageTime) {
                  logger.warn(
                    `Failed to parse timestamp for message ${msg.id}:`,
                    typeof msg.timestamp,
                    msg.timestamp
                  );
                  throw new Error(`Failed to parse timestamp for message ${msg.id}`);
                }

                return messageTime > twentyFourHoursAgo;
              } catch (error) {
                // Log specific error messages for debugging
                logger.warn(
                  `Failed to parse message timestamp for message ID ${msg.id}:`,
                  (error as Error).message
                );
                return true; // Keep the message if parsing fails
              }
            });

            if (validMessages.length !== state.messages.length) {
              state.loadMessages(validMessages);
            }
          }
        };
      },
    }
  )
);

// Compatibility hook for existing useMessages pattern
export const useMessages = () => {
  const { messages, loading: isLoading } = useMessagesStore();
  return { messages, isLoading };
};
